import axios from 'axios';

/**
 * Keeps the admin session alive.
 *
 * Cognito access tokens last 60 minutes; the refresh token lasts 30 days.
 * We swap the refresh token for a new access token shortly before expiry
 * (and once more if a request still comes back 401). Only when that fails is
 * the session really over: we clear it and tell the app to show the login
 * page with a "session expired" message.
 */

const API_URL = import.meta.env.VITE_API_URL;

/** Fired on window when the session can't be renewed. */
export const SESSION_EXPIRED_EVENT = 'admin:session-expired';
/** sessionStorage flag the login page reads to explain why you're there. */
export const SESSION_EXPIRED_FLAG = 'sessionExpired';

/** Refresh this long before the access token expires. */
const EXPIRY_MARGIN_MS = 2 * 60 * 1000;

/** Milliseconds since epoch when the JWT expires, or null if unreadable. */
export function tokenExpiry(token: string | null): number | null {
  if (!token) return null;
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(payload));
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

export function accessTokenExpiringSoon(): boolean {
  const exp = tokenExpiry(localStorage.getItem('accessToken'));
  return exp !== null && exp - Date.now() < EXPIRY_MARGIN_MS;
}

let inFlight: Promise<string | null> | null = null;

/**
 * Gets a new access token with the refresh token. Concurrent callers share one
 * request. Returns the new token, or null when the session can't be renewed.
 */
export function refreshSession(): Promise<string | null> {
  if (!inFlight) {
    inFlight = doRefresh().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

async function doRefresh(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;
  try {
    // Plain axios, not the app client, so this call never triggers itself.
    const { data } = await axios.post(`${API_URL}/api/v1/auth/refresh`, { refreshToken });
    if (!data?.accessToken) return null;
    localStorage.setItem('accessToken', data.accessToken);
    if (data.idToken) localStorage.setItem('idToken', data.idToken);
    if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
    return data.accessToken as string;
  } catch {
    return null;
  }
}

/** Ends the session and sends the app to the login page with a notice. */
export function expireSession(): void {
  for (const key of ['accessToken', 'idToken', 'refreshToken', 'userInfo']) {
    localStorage.removeItem(key);
  }
  try {
    sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1');
  } catch {
    // storage blocked; the redirect still happens
  }
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
}
